import type { EChartsInstance } from "echarts-for-react";
import type { TimeRange } from "./coordinateConversion";
import {
	type NearestPointLookup,
	findNearestRealPoint,
	isBlankAt,
} from "./nearestRealPoint";

type HighlightTarget = {
	seriesIndex: number;
	dataIndex: number;
};

const isSameTargets = (a: HighlightTarget[], b: HighlightTarget[]) =>
	a.length === b.length &&
	a.every(
		(target, i) =>
			target.seriesIndex === b[i].seriesIndex &&
			target.dataIndex === b[i].dataIndex,
	);

const findTargets = (
	lookup: NearestPointLookup,
	time: number,
	window: TimeRange,
): HighlightTarget[] => {
	const targets: HighlightTarget[] = [];

	for (const entry of Object.values(lookup)) {
		if (!entry.visible || !isBlankAt(entry.data, time)) continue;

		const nearest = findNearestRealPoint(entry.realPoints, time, window);
		if (!nearest) continue;

		targets.push({ seriesIndex: entry.seriesIndex, dataIndex: nearest.index });
	}

	return targets;
};

export const highlightNearestRealPoints = (
	chart: EChartsInstance,
	lookup: NearestPointLookup,
	window: TimeRange,
) => {
	const zr = chart.getZr();
	if (!zr) return;

	let highlighted: HighlightTarget[] = [];
	let frame = 0;

	const apply = (targets: HighlightTarget[]) => {
		if (chart.isDisposed() || isSameTargets(targets, highlighted)) return;

		if (highlighted.length > 0) {
			chart.dispatchAction({
				type: "downplay",
				notBlur: true,
				batch: highlighted,
			});
		}

		if (targets.length > 0) {
			chart.dispatchAction({
				type: "highlight",
				notBlur: true,
				batch: targets,
			});
		}

		highlighted = targets;
	};

	const resolveTargets = (offsetX: number) => {
		const time = Number(chart.convertFromPixel({ xAxisIndex: 0 }, offsetX));

		return Number.isFinite(time) ? findTargets(lookup, time, window) : [];
	};

	const handleMouseMove = (event: any) => {
		if (frame) return;

		const { offsetX } = event;
		frame = requestAnimationFrame(() => {
			frame = 0;
			apply(resolveTargets(offsetX));
		});
	};

	const handleGlobalOut = () => apply([]);

	zr.on("mousemove", handleMouseMove);
	zr.on("globalout", handleGlobalOut);

	return () => {
		if (frame) cancelAnimationFrame(frame);
		zr.off("mousemove", handleMouseMove);
		zr.off("globalout", handleGlobalOut);
		apply([]);
	};
};
